import { getText } from './taxInvoiceUtils';

const computeAmountExclVat = (row) => {
    const qty = parseFloat(row.quantity ?? row.qty ?? 0);
    const unitPrice = parseFloat(row.unit_price ?? row.price ?? 0);
    const discountPercent = parseFloat(row.discount_percentage ?? row.discount_percent ?? 0);
    const lineTotal = qty * unitPrice;
    const discountAmount = (lineTotal * discountPercent) / 100;
    return lineTotal - discountAmount;
};

const getItemName = (row) => (
    getText(row.item_description)
    || getText(row.item?.label)
    || getText(row.item?.name)
    || getText(row.item_name)
    || 'N/A'
);

const normalizeItem = (row, documentType) => {
    const name = getItemName(row);

    const unit = getText(row.unit) || getText(row.item?.unit) || 'PCS';
    const quantity = row.quantity ?? row.qty ?? 0;
    const unitPrice = row.unit_price ?? row.price ?? 0;

    let amountExclVat;
    if (documentType === 'cash_memo') {
        amountExclVat = computeAmountExclVat(row);
    } else {
        amountExclVat = row.amount_excl_vat ?? row.amount_excluding_vat ?? row.line_amount ?? computeAmountExclVat(row);
    }

    return { name, unit, quantity, unitPrice, amountExclVat };
};

const getItems = (raw, documentType) => {
    let source;
    if (documentType === 'sale_invoice') {
        source = Array.isArray(raw.grouped_items)
            ? raw.grouped_items
            : (Array.isArray(raw.sale_invoice_items) ? raw.sale_invoice_items : []);
    } else {
        source = Array.isArray(raw.items) ? raw.items : [];
    }
    return source.map((row) => normalizeItem(row, documentType));
};

const getInvoiceDate = (raw, documentType) => {
    if (documentType === 'cash_memo') {
        return raw.invoice_date || raw.delivery_date || '';
    }
    return raw.invoice_date || raw.date || raw.delivery_date || '';
};

const getLpoNumber = (raw, documentType) => {
    if (documentType === 'sale_invoice') {
        return getText(raw.customer_lpo) || getText(raw.lpo_number) || '';
    }
    if (documentType === 'cash_memo_direct') {
        return getText(raw.lpo_number) || '';
    }
    return getText(raw.lpo_number) || '';
};

const hasContactValue = (value) => {
    const text = getText(value).trim();
    return Boolean(text) && text !== '-';
};

const isPlaceholderAddressLine = (value) => {
    const text = getText(value).trim();
    if (!text) return true;

    const parts = text.split(',').map((part) => part.trim()).filter(Boolean);
    if (parts.length === 0) return true;

    return parts.every((part) => /^[^:]+:\s*-$/.test(part));
};

const getPrintDisplay = ({ address1, address2, country, phone, vatNumber }) => ({
    showAddress1: Boolean(getText(address1).trim()),
    showAddress2: Boolean(getText(address2).trim()) && !isPlaceholderAddressLine(address2),
    showCountry: Boolean(getText(country).trim()),
    showPhone: hasContactValue(phone),
    showVat: hasContactValue(vatNumber),
});

export const normalizeTaxInvoiceData = (raw, documentType) => {
    if (!raw) return null;

    const customerCode = documentType === 'cash_memo'
        ? ''
        : (getText(raw.customer_code) || getText(raw.customer?.code) || '');

    const address1 = getText(raw.address1) || '';
    const address2 = getText(raw.address2) || '';
    const country = getText(raw.country?.label) || getText(raw.country?.name) || '';
    const phone = getText(raw.phone_number) || getText(raw.customer?.phone) || '';
    const vatNumber = getText(raw.vat_number) || getText(raw.customer?.vat_number) || '';

    return {
        customerCode,
        customerName: getText(raw.customer_name) || getText(raw.customer?.label) || '',
        address1,
        address2,
        country,
        phone,
        vatNumber,
        invoiceNumber: getText(raw.invoice_number) || getText(raw.number) || '',
        invoiceDate: getInvoiceDate(raw, documentType),
        lpoNumber: getLpoNumber(raw, documentType),
        lpoDate: raw.lpo_date || '',
        items: getItems(raw, documentType),
        grossTotal: raw.gross_total,
        discountAmount: raw.discount_amount,
        subTotal: raw.sub_total,
        vatAmount: raw.vat_amount,
        netTotal: raw.net_total,
        printDisplay: getPrintDisplay({ address1, address2, country, phone, vatNumber }),
    };
};
